// Modal: detect shot changes in a local video file and pick styleframes.

import React, { useEffect, useRef, useState } from "react";
import { detectStyleframes, FrameCandidate, formatTimecode } from "../../lib/sceneDetect";
import { Modal, primaryButton, ProgressBar, secondaryButton } from "./ui";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Video to analyse: a local file, or the URL of the hosted file. */
  source: File | string | null;
  onConfirm: (frames: { blob: Blob; name: string; previewURL: string }[]) => void;
}

export default function FrameExtractor({ open, onClose, source, onConfirm }: Props) {
  const [file, setFile] = useState<File | string | null>(source);
  const [frames, setFrames] = useState<FrameCandidate[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState(0);
  const [found, setFound] = useState(0);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (open) setFile(source);
  }, [open, source]);

  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      setFrames((prev) => {
        prev.forEach((f) => URL.revokeObjectURL(f.previewURL));
        return [];
      });
      setSelected(new Set());
      setProgress(0);
      setError(null);
      setRunning(false);
    }
  }, [open]);

  const run = async (input: File | string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setRunning(true);
    setError(null);
    setFrames([]);
    setProgress(0);
    try {
      const result = await detectStyleframes(input, {
        signal: controller.signal,
        onProgress: (ratio, count) => {
          setProgress(ratio);
          setFound(count);
        },
      });
      setFrames(result);
      setSelected(new Set(result.map((f) => f.id)));
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        setError(
          err instanceof Error
            ? err.message
            : "Analyse impossible. Vérifiez que le fichier est une vidéo lisible (MP4 H.264 recommandé).",
        );
      }
    } finally {
      setRunning(false);
    }
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const confirm = () => {
    const kept = frames.filter((f) => selected.has(f.id));
    onConfirm(
      kept.map((f) => ({
        blob: f.blob,
        name: `frame-${formatTimecode(f.time).replace(":", "m")}s`,
        previewURL: URL.createObjectURL(f.blob),
      })),
    );
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title="Extraire des styleframes depuis la vidéo"
      footer={
        <>
          <span className="mr-auto self-center text-sm text-neutral-600">
            {frames.length > 0 && `${selected.size} image${selected.size > 1 ? "s" : ""} sur ${frames.length}`}
          </span>
          <button type="button" className={secondaryButton} onClick={onClose}>
            Annuler
          </button>
          <button type="button" className={primaryButton} disabled={!selected.size || running} onClick={confirm}>
            Ajouter {selected.size || ""} styleframe{selected.size > 1 ? "s" : ""}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-neutral-600 max-w-3xl">
          L'analyse se fait dans votre navigateur : chaque changement de plan devient une image
          candidate. Rien n'est envoyé tant que vous n'enregistrez pas la vidéo. Réservé aux
          fichiers fournis par les studios : télécharger une vidéo YouTube ou Vimeo pour en extraire
          des images est interdit par leurs conditions d'utilisation.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <label className={secondaryButton + " cursor-pointer"}>
            Choisir un fichier vidéo
            <input
              type="file"
              accept="video/*"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setFile(f);
                  run(f);
                }
              }}
            />
          </label>
          {file && (
            <>
              <span className="text-sm text-neutral-700 truncate max-w-xs">
                {typeof file === "string" ? "Fichier hébergé de la vidéo" : file.name}
              </span>
              {!running && (
                <button type="button" className={primaryButton} onClick={() => run(file)}>
                  {frames.length ? "Relancer l'analyse" : "Lancer l'analyse"}
                </button>
              )}
            </>
          )}
          {running && (
            <button type="button" className={secondaryButton} onClick={() => abortRef.current?.abort()}>
              Arrêter
            </button>
          )}
        </div>

        {running && (
          <ProgressBar
            value={progress}
            label={progress < 0.8 ? `Recherche des changements de plan… ${found} trouvé${found > 1 ? "s" : ""}` : "Capture des images…"}
          />
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}

        {frames.length > 0 && (
          <>
            <div className="flex gap-3 text-sm">
              <button type="button" className="underline" onClick={() => setSelected(new Set(frames.map((f) => f.id)))}>
                Tout sélectionner
              </button>
              <button type="button" className="underline" onClick={() => setSelected(new Set())}>
                Tout désélectionner
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {frames.map((frame) => {
                const on = selected.has(frame.id);
                return (
                  <button
                    type="button"
                    key={frame.id}
                    onClick={() => toggle(frame.id)}
                    aria-pressed={on}
                    className={`relative rounded-md overflow-hidden border-2 text-left transition ${
                      on ? "border-neutral-900" : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={frame.previewURL} alt={`Plan à ${formatTimecode(frame.time)}`} className="w-full aspect-video object-cover bg-neutral-100" />
                    <span className="absolute bottom-1.5 left-1.5 text-[11px] font-mono bg-black/70 text-white px-1.5 py-0.5 rounded">
                      {formatTimecode(frame.time)}
                    </span>
                    <span
                      className={`absolute top-1.5 right-1.5 w-5 h-5 rounded border-2 flex items-center justify-center text-xs ${
                        on ? "bg-neutral-900 border-neutral-900 text-white" : "bg-white/80 border-neutral-400"
                      }`}
                      aria-hidden="true"
                    >
                      {on ? "✓" : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
